import React, { useState, useEffect } from "react";
import serviceApi from "../services/serviceApi";
import { useNavigate } from "react-router-dom";
import environment from "../environments/environment.prod";


interface Student {
    name: string;
    clss: string;
    phone1?: string;
    imageUrl?: string;
    course?: string;
}

interface ApiResponse {
    status: string;
    message: string;
    objectData?: {
        token: string;
        name: string;
        logoUrl: string;
        students: Student[];
        appConfig?: { [key: string]: string };
    };
}



const Login: React.FC = () => {

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [showRegisterForm, setShowRegisterForm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // Backend data    
    const [logo, setLogo] = useState("");
    const [background, setBackground] = useState("");
    const [brandMsg, setBrandmsg] = useState("");

    const navigate = useNavigate();

    useEffect(() => {
        const fetchHomePage = async () => {
            try {
                const data = await serviceApi.get<{
                    status: string;
                    message: string;
                    objectData: {
                        bannerUrl: string;
                        instantMessage: string;
                        logoUrl: string;
                        name: string;
                        tchrNoteBrandMsg: string;
                    };
                }>(
                    "qPassApi", { serviceName: 'homePage' }
                );

                setLogo(data.objectData.logoUrl);
                setBackground(data.objectData.bannerUrl);
                setBrandmsg(data.objectData.tchrNoteBrandMsg);

            } catch (err) {
                console.error("Failed to fetch home page data", err);
            } finally {
                setIsInitialLoading(false);
            }
        };

        fetchHomePage();
    }, []);


    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!username || !password) {
            setError("Please enter both ID and Password");
            return;
        }

        setIsLoading(true);
        setError("");

        try {
            // Call authenticate API 
            const data = await serviceApi.put<ApiResponse>(
                "qpass?serviceName=authenticate",
                {
                    qpassId: username,
                    password: password,
                    parentAppDeviceId: "1234",
                    instance: "DEMO-TCHR-1500",
                }
            );
            if (data.status === "SUCCESS" && data.objectData) {
                const profile = data.objectData.students?.[0];

                localStorage.setItem("authData", JSON.stringify(profile));
                localStorage.setItem("access-token", data.objectData.token);
                localStorage.setItem("instance", "DEMO-TCHR-1500");
                localStorage.setItem("allStudents", JSON.stringify(data.objectData.students || []));
                localStorage.setItem("backendResponse", JSON.stringify(data));

                const appConfig = data.objectData.appConfig || {};
                localStorage.setItem("appConfig", JSON.stringify(appConfig));

                try {
                    const dashboardData = await serviceApi.get(environment.dashboard);
                    localStorage.setItem(
                        "dashboardResponse",
                        JSON.stringify(dashboardData)
                    );
                } catch (err) {
                    console.error("Dashboard API failed", err);
                }
                navigate("/Studentlists", {
                    state: { background },
                });
            }
            else {
                setError(data.message || "Invalid ID or Password");
            }
        } catch (err) {
            console.error(err);
            setError("Something went wrong during login.");
        } finally {
            setIsLoading(false);
        }
    };


    // Show initial loading spinner while fetching home page data
    if (isInitialLoading) {
        return (
            <div className="loading-screen">
                <div className="spinner spinner--lg"></div>
                <p className="loading-text">Loading...</p>
            </div>
        );
    }

    const inputStyle = {
        padding: "16px 20px",
        border: "none",
        borderRadius: "40px",
        outline: "none",
        fontSize: "16px",
        background: "#f5f5f5",
    };

    return (
        <div
            className={`login-container ${background ? 'dynamic-background' : ''}`}
            style={{
                backgroundImage: background ? `url(${background})` : "none",
            }}
        >
            <div className="login-form">
                {logo && (
                    <div className="logo">
                        <img src={logo} alt="Logo" />
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="input-group">
                        <i className="fas fa-user icon"></i>
                        <input
                            type="text"
                            placeholder="ID"
                            value={username}
                            onChange={(event) => setUsername(event.target.value)}
                            disabled={isLoading}
                        />
                    </div>

                    <div className="input-group">
                        <i className="fas fa-lock icon"></i>
                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            disabled={isLoading}
                        />
                    </div>

                    <button type="submit" className="login-button" disabled={isLoading}>
                        {isLoading ? 'Logging in...' : 'Login'}
                    </button>

                    {error && <p className="error">{error}</p>}
                </form>

                <p className="no-id-text">Don't have QPASS ID?</p>
                <button
                    className="register-button"
                    onClick={() => setShowRegisterForm(true)}
                    disabled={isLoading}
                >
                    <i className="fas fa-user-plus"></i> Register
                </button>

                {/* Future Register form from backend */}
                {showRegisterForm && (
                    <div className="register-form"
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: "rgba(0,0,0,0.5)",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 999,
                            margin: "auto",
                            borderRadius: "25px",
                            boxSizing: "border-box",
                        }}>

                        <div
                            style={{
                                width: "90%",
                                maxWidth: "450px",
                                background: "#fff",
                                borderRadius: "20px",
                                padding: "25px",
                                maxHeight: "90vh",
                                overflowY: "auto",
                            }}
                        >
                            <h2>Register Now</h2>
                            <form
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "5px",
                                }}>
                                <input
                                    type="text"
                                    placeholder="Student Name"
                                    style={inputStyle} />
                                <input
                                    type="text"
                                    placeholder="Class"
                                    style={inputStyle} />
                                <input
                                    type="text"
                                    placeholder="Current School"
                                    style={inputStyle} />
                                <input
                                    type="text"
                                    placeholder="Father Name"
                                    style={inputStyle} />
                                <input
                                    type="text"
                                    placeholder="Mother Name"
                                    style={inputStyle} />
                                <input
                                    type="tel"
                                    placeholder="Mobile Number"
                                    style={inputStyle} />
                                <button
                                    type="submit"
                                    style={{
                                        padding: "10px",
                                        border: "none",
                                        borderRadius: "40px",
                                        background: "#f5821f",
                                        color: "#fff",
                                        fontSize: "20px",
                                        fontWeight: "700",
                                        cursor: "pointer",
                                    }}>
                                    Register
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowRegisterForm(false)}
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        color: "#666",
                                        fontSize: "16px",
                                        cursor: "pointer",
                                    }}>
                                    Cancel
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </div>
            {/* {brandMsg && <p className="brand-message">{brandMsg}</p>} */}
            {brandMsg && !showRegisterForm && (<p className="brand-message">{brandMsg}</p>)}
        </div>

    );
};

export default Login;
